import { useSyncExternalStore } from 'react'
import { callMachine, type CallState } from './callMachine'

/**
 * LECTURE SÉLECTIVE de la machine à états.
 *
 * `useCallState()` rend l'état COMPLET : tout composant qui l'appelle se
 * redessine à chaque écriture, y compris celles qui ne le concernent pas. Or
 * la machine écrit une fois par seconde pendant un rebours d'auto-dial
 * (`preview.remainingSeconds`). Branchée sur l'état complet, la scène entière
 * — fiche contact, script, onglets, historique — se re-rendait à chaque
 * seconde du compte à rebours.
 *
 * Contrainte de perf du cahier : « le tic ne re-rend QUE son composant
 * compteur, jamais la scène ». D'où ce sélecteur.
 *
 * ⚠️ CONTRAT : le sélecteur doit rendre une valeur COMPARABLE PAR IDENTITÉ —
 * un nombre, une chaîne, un booléen, `null`, ou une référence stable. Rendre
 * un objet fabriqué à la volée (`s => ({ a: s.a })`) ferait boucler
 * `useSyncExternalStore` à l'infini : il compare avec `Object.is`. Pour lire
 * plusieurs champs, appeler plusieurs sélecteurs.
 */
export function useCallSelector<T>(selector: (state: CallState) => T): T {
  return useSyncExternalStore(
    callMachine.subscribe,
    () => selector(callMachine.getSnapshot()),
    () => selector(callMachine.getSnapshot()),
  )
}

/** Phase courante — le sélecteur le plus utilisé de la scène. */
export function useCallPhase(): CallState['phase'] {
  return useCallSelector((state) => state.phase)
}
